import { createReducer } from 'typesafe-actions'
import { IUserForReassignIntroducer, IUser, ReadUserReferralsResponseDto } from '@sparling/financial-common'
import { ActionTypes } from '../types'
import { LoadingStateEnum } from '../../types'
import {
  getIntroducerUsersFailure,
  getIntroducerUsersRequest,
  getIntroducerUsersSuccess,
  fetchUsersForReassignRequest,
  fetchUsersForReassignSuccess,
  fetchUsersForReassignFailure,
  assignIntroducerUserRequest,
  assignIntroducerUserSuccess,
  assignIntroducerUserFailure,
  reassignIntroducerUserRequest,
  reassignIntroducerUserSuccess,
  reassignIntroducerUserFailure,
  deleteIntroducerUserRequest,
  deleteIntroducerUserSuccess,
  deleteIntroducerUserFailure,
  getAssignableReferralsRequest,
  getAssignableReferralsSuccess,
  getAssignableReferralsFailure,
  canUpdateUserDetails,
  introducerDataUpdateSuccess,
} from '../actions'

const initialMetaState = {
  currentPage: 0,
  itemCount: 0,
  itemsPerPage: 0,
  totalItems: 0,
  totalPages: 0,
}

export type IntroducerState = {
  canUpdate: boolean
  userId: number | null
  users: {
    loading: LoadingStateEnum
    data: IUserForReassignIntroducer[]
    error: Error | null
  }
  assignableReferrals: {
    loading: LoadingStateEnum
    data: IUser[]
    error: Error | null
  }
  referredUsers: {
    loading: LoadingStateEnum
    data: ReadUserReferralsResponseDto
    error: Error | null
  }
}

export const initialState = {
  canUpdate: false,
  userId: null,
  users: {
    loading: LoadingStateEnum.IDLE,
    data: [],
    error: null,
  },
  assignableReferrals: {
    loading: LoadingStateEnum.IDLE,
    data: [],
    error: null,
  },
  referredUsers: {
    loading: LoadingStateEnum.IDLE,
    data: {
      items: [],
      meta: initialMetaState,
    },
    error: null,
  },
}

export const introducer = createReducer<IntroducerState, ActionTypes>(initialState)
  .handleAction(getIntroducerUsersRequest, (state, { payload }) => ({
    ...state,
    userId: payload.introducerId || null,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getIntroducerUsersSuccess, (state, { payload }) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getIntroducerUsersFailure, (state, { payload }) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.ERROR,
      data: initialState.referredUsers.data,
      error: payload,
    },
  }))
  .handleAction(fetchUsersForReassignRequest, (state) => ({
    ...state,
    users: {
      ...state.users,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(fetchUsersForReassignSuccess, (state, { payload }) => ({
    ...state,
    users: {
      ...state.users,
      loading: LoadingStateEnum.SUCCESS,
      data: payload.items,
    },
  }))
  .handleAction(fetchUsersForReassignFailure, (state, { payload }) => ({
    ...state,
    users: {
      ...state.users,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(getAssignableReferralsRequest, (state) => ({
    ...state,
    assignableReferrals: {
      ...state.assignableReferrals,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getAssignableReferralsSuccess, (state, { payload }) => ({
    ...state,
    assignableReferrals: {
      ...state.assignableReferrals,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getAssignableReferralsFailure, (state, { payload }) => ({
    ...state,
    assignableReferrals: {
      ...state.assignableReferrals,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(assignIntroducerUserRequest, (state) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(assignIntroducerUserSuccess, (state) => ({
    ...state,
    canUpdate: true,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.SUCCESS,
    },
  }))
  .handleAction(assignIntroducerUserFailure, (state) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.ERROR,
    },
  }))
  .handleAction(reassignIntroducerUserRequest, (state) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(reassignIntroducerUserSuccess, (state) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.SUCCESS,
    },
  }))
  .handleAction(reassignIntroducerUserFailure, (state) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.ERROR,
    },
  }))
  .handleAction(deleteIntroducerUserRequest, (state) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(deleteIntroducerUserSuccess, (state) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.SUCCESS,
    },
  }))
  .handleAction(deleteIntroducerUserFailure, (state, { payload }) => ({
    ...state,
    referredUsers: {
      ...state.referredUsers,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(introducerDataUpdateSuccess, (state, { payload }) => ({ ...state, canUpdate: payload.success }))
  .handleAction(canUpdateUserDetails, (state, { payload }) => ({ ...state, canUpdate: payload }))
